import {
  type Application,
  type ColorSource,
  Container,
  Graphics,
  Text as PixiText,
  RenderTexture,
  TextStyle,
  type TextStyleFill
} from "pixi.js"
import { useCallback, useEffect, useMemo, useState } from "react"
import { computeTextBoxLayout } from "./useTextBoxTexture.utils"

/**
 * Custom hook to generate a styled text box texture using Pixi.js.
 *
 * @param title - The text content to display inside the text box.
 * @param app - The Pixi.js Application instance used for rendering.
 * @param fontStyle - Partial text style configuration for the text.
 * @param backgroundColor - Background color of the text box.
 * @param textColor - Color of the text inside the text box.
 * @param borderRadius - Radius for rounded corners of the text box.
 * @param borderColor - Color of the border around the text box.
 * @param borderAlpha - Alpha (transparency) value for the border.
 * @param borderWidth - Width of the border (default: 3).
 * @param paddingX - Horizontal padding inside the text box (default: 16).
 * @param paddingY - Vertical padding inside the text box (default: 10).
 * @param oversample - Scaling factor for rendering quality (default: 2).
 * @param minWidth - Minimum width of the text box.
 *
 * @returns An object containing the generated texture, scale factor, and offsets.
 */
interface TextBoxTextureOptions {
  title: string
  app: Application
  fontStyle: Partial<TextStyle>
  backgroundColor: ColorSource
  textColor: TextStyleFill
  borderRadius: number
  borderColor: ColorSource
  borderAlpha: number
  borderWidth?: number
  paddingX?: number
  paddingY?: number
  scale?: number
  minWidth?: number
}

export function useTextBoxTexture({
  title,
  app,
  fontStyle,
  backgroundColor,
  textColor,
  borderRadius,
  borderColor,
  borderAlpha,
  borderWidth = 3,
  paddingX = 16,
  paddingY = 10,
  scale = 2,
  minWidth
}: TextBoxTextureOptions) {
  const [texture, setTexture] = useState<RenderTexture | null>(null)

  const resolution = window.devicePixelRatio || 1 // Use device pixel ratio for high-DPI screens
  const buffer = Math.ceil(borderWidth * scale) + 2 // Add buffer for border and rendering safety

  const fontFamily = useMemo(() => {
    return getComputedStyle(document.documentElement).getPropertyValue("--fr-body-font-family").trim()
  }, [])

  const createText = useCallback(() => {
    const style = new TextStyle({
      ...fontStyle,
      fontSize: (Number(fontStyle.fontSize) || 16) * scale,
      fill: textColor,
      fontFamily
    })

    const text = new PixiText(title, style)
    text.resolution = resolution * scale // Adjust resolution for high-DPI screens
    return text
  }, [fontStyle, scale, textColor, resolution, title, fontFamily])

  useEffect(() => {
    const container = new Container()
    const text = createText()

    const layout = computeTextBoxLayout({
      textWidth: text.width,
      textHeight: text.height,
      minWidth,
      paddingX,
      paddingY,
      borderWidth,
      scale
    })

    const bg = new Graphics()
    bg.lineStyle(layout.buffer, borderColor, borderAlpha)
    bg.beginFill(backgroundColor)
    bg.drawRoundedRect(layout.buffer, layout.buffer, layout.contentWidth, layout.contentHeight, borderRadius * scale)
    bg.endFill()
    container.addChild(bg)

    text.x = layout.textX
    text.y = layout.textY
    container.addChild(text)

    const rt = RenderTexture.create({
      width: layout.fullWidth,
      height: layout.fullHeight,
      resolution
    })

    app.renderer.render(container, { renderTexture: rt })
    setTexture(rt)

    return () => {
      rt.destroy(true)
      container.destroy({ children: true })
    }
  }, [
    app,
    resolution,
    scale,
    backgroundColor,
    borderRadius,
    borderColor,
    borderAlpha,
    minWidth,
    createText,
    paddingX,
    paddingY,
    borderWidth
  ])

  return {
    texture,
    scale: 1 / scale,
    offsetX: buffer / scale,
    offsetY: buffer / scale
  }
}
